/**
 * O365WriteActionDialog.jsx
 * 
 * Reusable dialog for O365 write actions (Send Email, Create Calendar Invite, Post to Teams, Save to OneDrive)
 * Automatically pre-populates fields from execution response metadata
 * Handles permission checks and user confirmation
 */

import React, { useState } from 'react';

const O365WriteActionDialog = ({
    isOpen,
    action, // { agent: "outlook", action: "send", needsPermission, prePopulated, ... }
    onSubmit, // (formData) => void
    onCancel, // () => void
    isSubmitting = false,
    theme = "theme-light",
}) => {
    const [formData, setFormData] = useState({});

    const isDark = theme === "theme-dark" || theme === "theme-contrast";
    const bg = isDark ? "#1f1f1f" : "#ffffff";
    const border = isDark ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.15)";
    const textColor = isDark ? "#ffffff" : "#1f1f1f";
    const muted = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)";
    const inputBg = isDark ? "rgba(255,255,255,0.08)" : "#fff";

    // Initialize form data from pre-populated fields
    React.useEffect(() => {
        if (action?.prePopulated) {
            setFormData(action.prePopulated);
        }
    }, [action]);

    if (!isOpen || !action) return null;

    const handleFieldChange = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleSubmit = () => {
        onSubmit?.(formData);
    };

    const renderFields = () => {
        const { agent, action: actionType } = action;
        const fields = [];

        if (agent === "outlook" && actionType === "send") {
            fields.push(
                { name: "to", label: "To", type: "email", required: true },
                { name: "cc", label: "Cc", type: "email", required: false },
                { name: "bcc", label: "Bcc", type: "email", required: false },
                { name: "subject", label: "Subject", type: "text", required: false },
                { name: "body", label: "Body", type: "textarea", required: false }
            );
        } else if (agent === "calendar" && actionType === "create") {
            fields.push(
                { name: "subject", label: "Subject", type: "text", required: true },
                { name: "startTime", label: "Start Time", type: "datetime-local", required: true },
                { name: "endTime", label: "End Time", type: "datetime-local", required: true },
                { name: "attendees", label: "Attendees (comma-separated)", type: "text", required: false },
                { name: "description", label: "Description", type: "textarea", required: false }
            );
        } else if (agent === "teams" && actionType === "post") {
            fields.push(
                { name: "destinationType", label: "Type", type: "select", options: ["chat", "channel"], required: true },
                { name: "destinationId", label: "Chat/Channel ID", type: "text", required: true },
                { name: "teamId", label: "Team ID (if channel)", type: "text", required: false },
                { name: "message", label: "Message", type: "textarea", required: true }
            );
        } else if (agent === "onedrive" && actionType === "save") {
            fields.push(
                { name: "fileName", label: "File Name", type: "text", required: true },
                { name: "folderId", label: "Folder ID", type: "text", required: false, disabled: true },
                { name: "content", label: "Content", type: "textarea", required: true }
            );
        }

        return fields.map(field => (
            <label key={field.name} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, marginBottom: 10 }}>
                <span style={{ color: muted }}>
                    {field.label}
                    {field.required ? <span style={{ color: "red" }}> *</span> : ""}
                </span>

                {field.type === "textarea" ? (
                    <textarea
                        value={String(formData[field.name] ?? "")}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        disabled={field.disabled || isSubmitting}
                        style={{
                            border,
                            borderRadius: 8,
                            padding: "8px 10px",
                            background: field.disabled ? "rgba(0,0,0,0.1)" : inputBg,
                            color: textColor,
                            fontFamily: "inherit",
                            minHeight: 80,
                            resize: "vertical"
                        }}
                    />
                ) : field.type === "select" ? (
                    <select
                        value={String(formData[field.name] ?? "")}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        disabled={field.disabled || isSubmitting}
                        style={{
                            border,
                            borderRadius: 8,
                            padding: "8px 10px",
                            background: inputBg,
                            color: textColor,
                            cursor: "pointer"
                        }}
                    >
                        <option value="">Select {field.label}...</option>
                        {field.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type={field.type}
                        value={String(formData[field.name] ?? "")}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        disabled={field.disabled || isSubmitting}
                        style={{
                            border,
                            borderRadius: 8,
                            padding: "8px 10px",
                            background: field.disabled ? "rgba(0,0,0,0.1)" : inputBg,
                            color: textColor
                        }}
                    />
                )}
            </label>
        ));
    };

    const getTitle = () => {
        const { agent, action: actionType } = action;
        if (agent === "outlook" && actionType === "send") return "📧 Send Email";
        if (agent === "calendar" && actionType === "create") return "📅 Create Calendar Invite";
        if (agent === "teams" && actionType === "post") return "💬 Post to Teams";
        if (agent === "onedrive" && actionType === "save") return "💾 Save to OneDrive";
        return "O365 Action";
    };

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2300,
                padding: 16,
            }}
        >
            <div
                style={{
                    width: "min(600px, 96vw)",
                    maxHeight: "85vh",
                    overflowY: "auto",
                    borderRadius: 14,
                    background: bg,
                    border,
                    color: textColor,
                    padding: 20,
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 17 }}>{getTitle()}</div>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: textColor,
                            cursor: "pointer",
                            fontSize: 18
                        }}
                        disabled={isSubmitting}
                        title="Close"
                    >
                        ×
                    </button>
                </div>

                {action.message && (
                    <div style={{ color: muted, fontSize: 13, marginBottom: 12, padding: "8px 10px", background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", borderRadius: 8 }}>
                        {action.message}
                    </div>
                )}

                <div style={{ marginBottom: 16 }}>
                    {renderFields()}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        style={{
                            borderRadius: 8,
                            padding: "7px 11px",
                            border,
                            background: "transparent",
                            color: textColor,
                            cursor: "pointer"
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        style={{
                            borderRadius: 8,
                            padding: "7px 11px",
                            border: "none",
                            background: "#0078d4",
                            color: "#fff",
                            cursor: "pointer",
                            opacity: isSubmitting ? 0.6 : 1
                        }}
                    >
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default O365WriteActionDialog;
